import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { budgetActualApi } from '../lib/budget-actual-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { Granularity } from '../types/budget-actual.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実目標データを粒度指定で取得するフック。
 * @graph-connects budgetActualApi [calls] budget-actual API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useTargets(from?: string, to?: string, granularity?: Granularity) {
  return useQuery({
    queryKey: queryKeys.budgetActual.targets(from, to, granularity),
    queryFn: () => budgetActualApi.getTargets({ from, to, granularity }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 期間内のオファーの内訳（クーポン名ごと）を取得するフック。期間を省くと API の既定期間になる。enabled が false なら API を呼ばない（計測開始前の期間など）。キーは 'budget-actual' で始まり、同期の成功で取り直される。
 * @graph-connects budgetActualApi [calls] budget-actual API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useOfferBreakdown(from?: string, to?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.budgetActual.offerBreakdown(from, to),
    queryFn: () => budgetActualApi.getOfferBreakdown({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business スプシ→BQ同期を実行するミューテーションフック。成功後に 'budget-actual' で始まるキー（目標・実績とオファーの内訳）を無効化。
 * @graph-connects budgetActualApi [calls] budget-actual API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useSyncTargets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => budgetActualApi.sync(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['budget-actual'] });
    },
  });
}
